/**
 * Per-property roster for the Feasibility section: which properties actually
 * trigger the requirement, and where each one's report stands.
 *
 * Two DIFFERENT signals sit side by side on purpose:
 *   • "Property criteria" — the computed `SubjectProperty.feasibility_required`
 *     dict (budget > $150k / change of use / expansion > 20% / budget > purchase
 *     price). This is what the required-documents CHECKLIST keys on.
 *   • "Order status"      — the stored `feasibilityReportStatus` select, which is
 *     what budget review's `ordered` keys on. Editable in place here; the
 *     property sidebar edits the same field.
 * A property can be required by the loan program with NO criterion met — the
 * section says so rather than pretending the two agree.
 *
 * Editing goes through the pinned-right action column, not a row click: the
 * property cell is itself a link into the property sidebar, so a row-click
 * editor would fire both.
 */
import EditOutlined from "@mui/icons-material/EditOutlined";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import {
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_GRID_TWO_LINE_ROW_H,
  CARD_TABLE_BOX_SX,
  GRID_HEADER_H,
} from "../../shared/cardGrid";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import selectOptionsFeasibilityReportStatus from "../../../../constants/selectOptions/selectOptionsFeasibilityReportStatus";
import { useFetchedSelectionsRequestsHook } from "../../../../hooks/useFetchedSelectionsRequestsHook";
import { useModelFieldOptions } from "../../../../hooks/useModelFieldOptions";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../../redux/reducers/detailSidebarReducer";
import {
  actionColumn,
  ActionCell,
} from "../../../admin/shared/adminTableActions";
import { T } from "./budgetDrawsTheme";

/** Property + the "marked received, nothing on file" warning under it. */
const PropertyCell = (p) => {
  const row = p.data;
  const dispatch = useDispatch();
  const open = (e) => {
    e.stopPropagation();
    if (!row?.property?._id) return;
    dispatch(
      detailSidebarOpen(
        DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
        row.property,
        row.property._id,
      ),
    );
  };
  return (
    <Flx column jc sx={{ height: "100%", minWidth: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={open}
        sx={{
          border: 0,
          background: "none",
          p: 0,
          textAlign: "left",
          fontFamily: "inherit",
          fontSize: 13,
          fontWeight: 600,
          color: T.ink,
          cursor: "pointer",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: 1.4,
          "&:hover": { color: T.accentInk, textDecoration: "underline" },
        }}
      >
        {row.label}
      </Box>
      {row.conflict && (
        <Flx ac g={0.5}>
          <ErrorOutlineRounded sx={{ fontSize: 13, color: T.amberInk }} />
          <Txt sx={{ fontSize: 11.5, color: T.amberInk, lineHeight: 1.4 }}>
            Marked {String(row.statusValue).toLowerCase()}, no report on file
          </Txt>
        </Flx>
      )}
    </Flx>
  );
};

/** The `feasibility_required` criteria that are actually true, with figures. */
const CriteriaCell = (p) => {
  const row = p.data;
  return (
    <Flx ac g={0.625} sx={{ height: "100%", flexWrap: "wrap", minWidth: 0 }}>
      {row.criteria.length ? (
        row.criteria.map((c) => (
          <Box
            key={c.key}
            sx={{
              px: 1,
              py: "3px",
              borderRadius: "6px",
              background: T.accentTint,
              color: T.accentInk,
              fontSize: 11.5,
              fontWeight: 600,
              lineHeight: 1.4,
            }}
          >
            {c.label}
          </Box>
        ))
      ) : (
        <Txt sx={{ fontSize: 12.5, color: T.faint }}>
          {row.required ? "None met · required by program" : "None met"}
        </Txt>
      )}
    </Flx>
  );
};

/** Inline editor for `feasibilityReportStatus`. */
const StatusCell = (p) => {
  const row = p.data;
  const dispatch = useDispatch();
  const { updateSubjectProperty } = useUnderwritingHook();
  const [saving, setSaving] = useState(false);

  const commit = (value) => {
    if (!row?.dealId || !row?.property?._id || value === row.statusValue)
      return;
    setSaving(true);
    updateSubjectProperty({
      loanId: row.dealId,
      propertyId: row.property._id,
      data: { feasibilityReportStatus: value },
      onSuccessFn: (d) => dispatch(loanDrilldownSet(d)),
      onCompleteFn: () => setSaving(false),
      onFailFn: () => setSaving(false),
    });
  };

  return (
    <Flx ac g={0.75} sx={{ height: "100%" }}>
      <Select
        size="small"
        displayEmpty
        value={row.statusValue || ""}
        disabled={saving}
        onChange={(e) => commit(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        renderValue={(v) =>
          v || (
            <Box component="span" sx={{ color: T.faint }}>
              Not set
            </Box>
          )
        }
        sx={{
          minWidth: 138,
          height: 30,
          fontSize: 12.5,
          background: "#fff",
          borderRadius: "7px",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: T.line },
        }}
      >
        {row.options.map((opt) => {
          const value = typeof opt === "string" ? opt : opt.value;
          const label = typeof opt === "string" ? opt : opt.label || opt.value;
          return (
            <MenuItem key={value} value={value} sx={{ fontSize: 12.5 }}>
              {label}
            </MenuItem>
          );
        })}
      </Select>
      {saving && (
        <CircularProgress size={12} thickness={5} sx={{ color: T.faint }} />
      )}
    </Flx>
  );
};

/** Pinned-right edit affordance — opens the full property sidebar. */
const EditCell = (p) => {
  const row = p.data;
  const dispatch = useDispatch();
  return (
    <ActionCell>
      <Tooltip title="Open property">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (!row?.property?._id) return;
            dispatch(
              detailSidebarOpen(
                DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
                row.property,
                row.property._id,
              ),
            );
          }}
        >
          <EditOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </ActionCell>
  );
};

const FeasibilityPerPropertyTable = ({
  dealId,
  rows,
  conflictIds,
  required,
}) => {
  // Values are matched by string equality server-side, so they come from the
  // model schema. Only the property sidebar requests the subjectProperty schema,
  // and this tab can be opened without ever visiting it — so ask for it here
  // rather than silently living on the fallback.
  const schema = useSelector(
    (s) => s.fetchedSelections?.["modelSchema:subjectProperty"],
  );
  const { getModelSelectOptions } = useFetchedSelectionsRequestsHook();
  useEffect(() => {
    if (!schema) getModelSelectOptions({ objectType: "subjectProperty" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  const options = useModelFieldOptions(
    "feasibilityReportStatus",
    selectOptionsFeasibilityReportStatus,
    { objectType: "subjectProperty" },
  );

  const rowData = useMemo(
    () =>
      (rows || []).map((r) => ({
        ...r,
        dealId,
        required,
        options,
        conflict: Boolean(conflictIds?.has(r.id)),
      })),
    [rows, dealId, required, options, conflictIds],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Property",
        colId: "property",
        field: "label",
        flex: 2.2,
        minWidth: 220,
        cellRenderer: PropertyCell,
      },
      {
        headerName: "Property criteria",
        colId: "criteria",
        // Sort/filter on something meaningful: how many criteria are met.
        valueGetter: (p) => p.data?.criteria?.length || 0,
        flex: 2.4,
        minWidth: 240,
        cellRenderer: CriteriaCell,
      },
      {
        headerName: "Order status",
        colId: "status",
        field: "statusValue",
        width: 180,
        cellRenderer: StatusCell,
      },
      actionColumn({ colId: "open", cellRenderer: EditCell }),
    ],
    [],
  );

  if (!rows?.length) return null;

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Flx ac g={0.625} sx={{ mb: 1.25 }}>
        <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>
          Per property
        </Txt>
        <Tooltip title="Criteria come from the property's computed feasibility_required (budget over $150k, change of use, expansion over 20%, budget over purchase price). Order status is the UW-maintained field budget review reads.">
          <InfoOutlined sx={{ fontSize: 14, color: T.faint }} />
        </Tooltip>
      </Flx>

      {/* Same table system as the Experience ledger and the calc tabs: the
          bordered rounded block plus the shared card-grid look (small-caps
          faint headers, hairline row rules, no column separators), so a table
          reads the same wherever it sits on the drilldown. See shared/cardGrid. */}
      <Box sx={CARD_TABLE_BOX_SX}>
        <AgGridTableWrapper
          compactTable
          noBorder
          suppressMinHeightWhenPopulated={rowData}
          sx={CARD_GRID_SX}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={CARD_GRID_COL_DEF}
            getRowId={(p) => p.data.id}
            domLayout="autoHeight"
            rowHeight={CARD_GRID_TWO_LINE_ROW_H}
            headerHeight={GRID_HEADER_H}
            suppressCellFocus
          />
        </AgGridTableWrapper>
      </Box>
    </Box>
  );
};

FeasibilityPerPropertyTable.propTypes = {
  dealId: PropTypes.string,
  rows: PropTypes.array,
  /** Set of row ids marked received/reviewed with nothing on file. */
  conflictIds: PropTypes.object,
  required: PropTypes.bool,
};

export default FeasibilityPerPropertyTable;
